import { Box, Center, Flex, Spacer, Stack, Text } from '@chakra-ui/react'
import { DateTime } from 'luxon'
import { useCallback, useMemo } from 'react'

import chakraTokenToVar from '@/utils/chakraTokenToVar'
import { getRelativeTimeShort } from '@/utils/time'

import { useUser } from '@/client/context/user'
import useClientNow from '@/client/hooks/useClientNow'
import type { MessageType } from '@/client/services/conversation'
import { getMessagePlainText } from '@/client/services/conversation/getMessagePlainText'
import type { ConversationListItemConversationFragment } from '@/client/services/hasura/generated/graphql'
import getBodyForHiddenEntity from '@/client/services/hidden/getBodyForHiddenEntity'

import CountBubble from '@/components/Worlds/UI/CountBubble'
import { withErrorBoundary } from '@/components/Worlds/UI/ErrorBoundary'

import ConversationAvatars from './ConversationAvatars'
import ConversationTitle from './ConversationTitle'

export type ConversationListItemProps = {
  conversation: ConversationListItemConversationFragment
  isActive: boolean
  onClick: (externalUuid: string) => void
}

function ConversationListItemInternal({
  conversation,
  isActive,
  onClick,
}: ConversationListItemProps): React.ReactElement {
  const user = useUser()
  const [now] = useClientNow()

  const lastMessage = conversation.last_message[0]
  const currentConversationUser = conversation.current_conversation_user[0]

  const lastMessageCreatedAt = useMemo(
    () => (lastMessage == null ? null : DateTime.fromISO(lastMessage.created_at)),
    [lastMessage]
  )
  const currentConversationUserLastReadAt = useMemo(
    () =>
      currentConversationUser == null
        ? null
        : DateTime.fromISO(`${currentConversationUser.last_read_at}`),
    [currentConversationUser]
  )

  const messageUserDisplayName =
    lastMessage?.user?.id === user.id ? 'You' : (lastMessage?.user?.virtual_display_name ?? null)

  const isUnread =
    lastMessageCreatedAt != null &&
    currentConversationUserLastReadAt != null &&
    lastMessageCreatedAt > currentConversationUserLastReadAt &&
    lastMessage.user?.id !== user.id &&
    !isActive

  const onClickHandler = useCallback(() => {
    if (!isActive) {
      onClick?.(conversation.external_uuid)
    }
  }, [isActive, onClick, conversation])

  return (
    <Stack
      borderBottom={`2px solid ${chakraTokenToVar('border.muted')}`}
      _hover={{ bg: isActive ? 'bg.muted' : 'bg.subtle' }}
      cursor="pointer"
      color="fg"
      textDecoration="none"
      px="24px"
      bg={isActive ? 'bg.muted' : 'bg'}
      direction="row"
      spacing="16px"
      align="center"
      width="100%"
      height="100px"
      onClick={onClickHandler}
    >
      <Box>
        <ConversationAvatars conversation={conversation} size="md" />
      </Box>
      <Stack direction="column" spacing="0px" flex={1} overflowX="hidden">
        <ConversationTitle conversation={conversation} />
        {lastMessage == null ? (
          <Box height="24px" />
        ) : (
          <Stack direction="row" spacing="8px" justify="space-between" align="center">
            <Text
              noOfLines={1}
              color={isUnread ? 'fg' : 'fg.muted'}
              fontWeight={isUnread ? 'bold' : 'normal'}
              fontStyle={
                lastMessage.hidden_at != null ||
                (lastMessage.type as MessageType) === 'operator-plain-v1'
                  ? 'italic'
                  : 'normal'
              }
            >
              {messageUserDisplayName}
              {messageUserDisplayName == null ? null : ': '}
              {lastMessage.type !== 'deleted-user-v1' && lastMessage.hidden_at != null
                ? getBodyForHiddenEntity('message', 'plain-text')
                : getMessagePlainText({
                    type: lastMessage.type as MessageType,
                    body: lastMessage.body_safe,
                  })}
            </Text>
            <Spacer />
            {lastMessageCreatedAt == null ? null : (
              <Text color="fg.muted">
                {getRelativeTimeShort({ time: lastMessageCreatedAt, now })}
              </Text>
            )}
          </Stack>
        )}
      </Stack>
      <Flex justify="flex-end" width="16px" height="16px">
        {isUnread ? <CountBubble position="static" /> : null}
      </Flex>
    </Stack>
  )
}

const ConversationListItem = withErrorBoundary(ConversationListItemInternal, () => (
  <Center
    borderBottom={`2px solid ${chakraTokenToVar('border.muted')}`}
    cursor="pointer"
    px="24px"
    height="100px"
  >
    <Text color="fg.muted">Oops! There was an error displaying this conversation.</Text>
  </Center>
))

export default ConversationListItem
